import { ProjectRolesList } from "@/containers/Admin/ProjectRoles";
import { ProjectRoleProvider } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { EditProjectRolePage } from "@/containers/Admin/ProjectRoles/containers/EditProjectRolePage";
import { NewProjectRolePage } from "@/containers/Admin/ProjectRoles/containers/NewProjectRolePage";
import { Profile } from "@/containers/Profile";
import { ProjectTeamPage } from "@/containers/Project/components/ProjectTeam/containers/ProjectTeamPage";
import {
  ProjectTemplateEditor,
  ProjectTemplates,
} from "@/containers/ProjectTemplate";
import Swagger from "@/containers/Swagger";
import { useAllRights } from "@/hooks";
import { ProjectProvider } from "@/providers";
import { Layout } from "antd";
import { ErrorBoundary } from "react-error-boundary";
import { useIntl } from "react-intl";
import { Outlet, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { PageWrapper, PermissionEditor } from "./components";
import { ErrorResult } from "./components/ErrorResult";
import {
  AddApprovalWorkflow,
  AddWorkflowApprover,
  AdminConfigPage,
  ApprovalWorkflows,
  BaselineDates,
  ConditionalAccessGroup,
  Dashboard,
  DownloadFile,
  EditApprovalWorkflow,
  EditBaselineDate,
  EditViewProjectIntake,
  Initiative,
  InitiativeList,
  ListConditionalAccessGroups,
  MainLayout,
  NotificationGroups,
  Permissions,
  Project,
  ProjectHistory,
  RisksAndIssues,
  ProjectList,
  UserReplacement,
  UserReplacementHistory,
  Users,
  VpinHistory,
} from "./containers";
import {
  PendingIntakeApprovalsFullView,
  PendingMilestoneApprovalsFullView,
  RecentlyApprovedIntakeFullView,
} from "./containers/Dashboard";
import {
  RecentlyApprovedMilestonesFullView,
  RoutingMilestoneApprovalsFullView,
} from "./containers/Dashboard/FullViews";
import {
  EditForm,
  EditFormField,
  FormSelection,
  NewFormField,
} from "./containers/Forms";

const Pages = () => {
  const intl = useIntl();
  const {
    canViewUsers,
    canCreateIntake,
    canViewCreateManageInitiatives,
    canViewPermissions,
    canEditPermissions,
    canViewProjectRoles,
    canEditProjectRoles,
    canViewForms,
    canViewApprovalWorkFlows,
    canViewTemplates,
    canViewNotificationGroups,
    canViewBaselineDates,
    canAccessViper,
    canViewUserReplacement,
    canViewApiDocumentation
  } = useAllRights();

  return (
    <Routes>
      <Route
        element={
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ErrorResult
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
            errorHandler={(error, errorInfo) => {
              console.error("Error Occurred", error, errorInfo);
            }}
          >
            <MainLayout />
          </ErrorBoundary>
        }
      >
        <Route
          index
          element={
            <ProtectedRoute authorized={canAccessViper}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="dashboard">
          <Route
            path="pending-intake-approvals"
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <PendingIntakeApprovalsFullView />
              </ProtectedRoute>
            }
          />
          <Route
            path="pending-milestone-approvals"
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <PendingMilestoneApprovalsFullView />
              </ProtectedRoute>
            }
          />
          <Route
            path="recently-approved-intakes"
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <RecentlyApprovedIntakeFullView />
              </ProtectedRoute>
            }
          />
          <Route
            path="routing-milestone-approvals"
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <RoutingMilestoneApprovalsFullView />
              </ProtectedRoute>
            }
          />
          <Route
            path="recently-approved-milestones"
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <RecentlyApprovedMilestonesFullView />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route authorized={canAccessViper} path="projects">
          <Route
            index
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <ProjectList />
              </ProtectedRoute>
            }
          />
          <Route
            path={":projectId"}
            element={
              <ProtectedRoute authorized={canAccessViper}>
                <ProjectProvider>
                  <Outlet />
                </ProjectProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Project />} />
            <Route path="history" element={<ProjectHistory />} />
            <Route path="risks-and-issues" element={<RisksAndIssues />} />
            <Route path="vpinhistory" element={<VpinHistory />} />
            <Route path="team" element={<ProjectTeamPage />} />
            <Route path=":fileId" element={<DownloadFile />}>
              <Route path=":fileName" element={<DownloadFile />} />
            </Route>
          </Route>
          <Route path="intake">
            <Route
              path="new"
              element={
                <ProtectedRoute authorized={canCreateIntake}>
                  <EditViewProjectIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path=":projectId"
              element={
                <ProtectedRoute authorized={canAccessViper}>
                  <ProjectProvider>
                    <Outlet />
                  </ProjectProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<EditViewProjectIntake />} />
            </Route>
          </Route>
        </Route>
        <Route path="initiatives">
          <Route
            index
            element={
              <ProtectedRoute authorized={canViewCreateManageInitiatives}>
                <InitiativeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute authorized={canViewCreateManageInitiatives}>
                <Initiative />
              </ProtectedRoute>
            }
          />
          <Route
            path=":initiativePublicId"
            element={
              <ProtectedRoute authorized={canViewCreateManageInitiatives}>
                <Initiative />
              </ProtectedRoute>
            }
          />
       </Route>
        <Route path="admin">
          <Route path="api/v1">
            <Route
              index
              element={
                <ProtectedRoute authorized={canViewApiDocumentation}>
                  <Swagger />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="users"
            element={
              <ProtectedRoute authorized={canViewUsers}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<Users />} />
            <Route path=":id" element={<Profile />} />
          </Route>
          <Route
            element={
              <ProtectedRoute authorized={canViewPermissions}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route path="permissions">
              <Route index element={<Permissions />} />
              <Route path="new" element={<PermissionEditor />} />
              <Route path=":id" element={<PermissionEditor />} />
            </Route>

            <Route path="conditionalAccess">
              <Route index element={<ListConditionalAccessGroups />} />
              <Route
                path=":id"
                element={
                  <ProtectedRoute authorized={canEditPermissions}>
                    <ConditionalAccessGroup />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>

          <Route
            path="projectRoles"
            element={
              <ProtectedRoute authorized={canViewProjectRoles}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<ProjectRolesList />} />
            <Route
              path="new"
              element={
                <ProtectedRoute authorized={canEditProjectRoles}>
                  <ProjectRoleProvider>
                    <NewProjectRolePage />
                  </ProjectRoleProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedRoute authorized={canEditProjectRoles}>
                  <ProjectRoleProvider>
                    <EditProjectRolePage />
                  </ProjectRoleProvider>
                </ProtectedRoute>
              }
            />
          </Route>

          <Route
            path="forms"
            element={
              <ProtectedRoute authorized={canViewForms}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<FormSelection />} />
            <Route path=":formType">
              <Route index element={<EditForm />} />
              <Route path="fields">
                <Route path="new" element={<NewFormField />} />
                <Route path=":id" element={<EditFormField />} />
              </Route>
            </Route>
          </Route>
          <Route
            path="approvalWorkFlows"
            element={
              <ProtectedRoute authorized={canViewApprovalWorkFlows}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<ApprovalWorkflows />} />
            <Route path="new" element={<AddApprovalWorkflow />} />
            <Route path=":id">
              <Route index element={<EditApprovalWorkflow />} />
              <Route path="new">
                <Route index element={<AddWorkflowApprover />} />
                <Route path=":approverId" element={<AddWorkflowApprover />} />
              </Route>
            </Route>
          </Route>
          <Route
            path="baselineDates"
            element={
              <ProtectedRoute authorized={canViewBaselineDates}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<BaselineDates />} />
            <Route index element={<BaselineDates />} />
            <Route path=":projectTypeId/:baselineDateId">
              <Route index element={<EditBaselineDate />} />
            </Route>
            <Route path="create">
              <Route index element={<EditBaselineDate />} />
            </Route>
          </Route>
          <Route
            path="config"
            element={
              <ProtectedRoute authorized={canViewBaselineDates}>
                <AdminConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="projectTemplates"
            element={
              <ProtectedRoute authorized={canViewTemplates}>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route index element={<ProjectTemplates />} />
            <Route path="edit/:id" element={<ProjectTemplateEditor />} />
          </Route>
          <Route
            path="notificationGroups"
            element={
              <ProtectedRoute authorized={canViewNotificationGroups}>
                <NotificationGroups />
              </ProtectedRoute>
            }
          />
          <Route path="userReplacement">
            <Route
              index
              element={
                <ProtectedRoute authorized={canViewUserReplacement}>
                  <UserReplacement />
                </ProtectedRoute>
              }
            />
            <Route
              path="history"
              element={
                <ProtectedRoute authorized={canViewUserReplacement}>
                  <UserReplacementHistory />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
        <Route
          path="forbidden"
          element={
            <Layout>
              <ErrorResult error={{ response: { status: 403 } }} />
            </Layout>
          }
        />
        <Route
          path="*"
          element={
            <PageWrapper
              pageTitle={intl.formatMessage({
                id: "pageErrors.pageNotFoundTitle",
              })}
            >
              <ErrorResult error={{ response: { status: 404 } }} />
            </PageWrapper>
          }
        />
      </Route>
    </Routes>
  );
};

export default Pages;
